/*
 * The reviews filter as a dropdown, like the reviews sort beside it (Jenny
 * 2026-09-29, "make the filters a dropdown, like the sort so it's
 * consistent"). It replaces a boxed panel that slid open under the header.
 *
 * HOW IT DRIVES JUDGE.ME
 * Judge.me's filter is a panel of rating chips that its Vue app ADDS when the
 * filter button is pressed and REMOVES when it is pressed again, and removing
 * it also CLEARS the filter (measured on the live store). So the chosen
 * rating only holds while Judge.me's panel exists. This script therefore:
 *
 *   - catches every press of the filter button before Judge.me sees it and
 *     opens our list instead (the shared .custom-options panel, placed at the
 *     end of <body> like the sort's, where Judge.me's re-renders cannot reach
 *     it);
 *   - on a rating: opens Judge.me's panel if it is not open (it is hidden by
 *     custom.css, so nothing appears) and clicks that rating's own chip, so
 *     Judge.me does the filtering exactly as if the chip had been pressed;
 *   - on "All ratings": closes Judge.me's panel, which clears the filter.
 *
 * A side effect worth keeping: Judge.me marks its button active while its
 * panel is open, and the panel is only open while a rating is chosen, so the
 * button reads as "on" exactly when a filter is on.
 *
 * Rows are real <button>s, so the list works from the keyboard: Enter on the
 * filter button opens it with focus on the chosen row, Tab and the arrow keys
 * move, Escape closes and returns focus to the button.
 */
(function () {
  var BUTTON = 'button.jm-custom-answer-filter';
  var PANEL = '.jm-filter-panel';
  var RATINGS = ['5', '4', '3', '2', '1'];

  var list = null;
  var owner = null;
  var passThrough = false;

  function widgetOf(button) {
    return button.closest('.jm-review-widget') || document;
  }

  function judgePanel(button) {
    return widgetOf(button).querySelector(PANEL);
  }

  function currentRating(button) {
    var panel = judgePanel(button);
    var checked = panel && panel.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : '';
  }

  /* Hands one press to Judge.me, which toggles its own panel. */
  function pressForJudge(button) {
    passThrough = true;
    button.click();
    passThrough = false;
  }

  function whenPanelExists(button, then) {
    var tries = 0;
    (function check() {
      var panel = judgePanel(button);
      if (panel) return then(panel);
      if (++tries < 40) requestAnimationFrame(check);
    })();
  }

  function choose(button, value) {
    var panel = judgePanel(button);

    if (!value) {
      if (panel) pressForJudge(button);
      return;
    }
    if (currentRating(button) === value) return;

    if (!panel) pressForJudge(button);
    whenPanelExists(button, function (p) {
      var input = p.querySelector('input[type="radio"][value="' + value + '"]');
      var chip = input && input.closest('label');
      if (chip) chip.click();
    });
  }

  function row(label, value, selected) {
    var el = document.createElement('button');
    el.type = 'button';
    el.className = 'custom-option' + (selected ? ' selection' : '');
    el.dataset.value = value;
    el.setAttribute('role', 'menuitemradio');
    el.setAttribute('aria-checked', selected ? 'true' : 'false');
    el.appendChild(document.createTextNode(label));
    if (value) {
      var star = document.createElement('span');
      star.className = 'fj-filter-star';
      star.setAttribute('aria-hidden', 'true');
      star.textContent = '★';
      el.appendChild(star);
      el.setAttribute('aria-label', value === '1' ? '1 star' : value + ' stars');
    }
    return el;
  }

  function place(panel, anchor) {
    var r = anchor.getBoundingClientRect();
    var w = panel.offsetWidth;
    var gap = 8;
    var left = r.right - w;
    if (left < gap) left = gap;
    if (left + w > window.innerWidth - gap) left = Math.max(gap, window.innerWidth - gap - w);
    var top = r.bottom + gap;
    if (top + panel.offsetHeight > window.innerHeight - gap && r.top - gap - panel.offsetHeight > 0) {
      top = r.top - gap - panel.offsetHeight;
    }
    panel.style.left = left + 'px';
    panel.style.top = top + 'px';
  }

  function close(returnFocus) {
    if (!list) return;
    list.remove();
    list = null;
    if (owner) {
      owner.setAttribute('aria-expanded', currentRating(owner) ? 'true' : 'false');
      if (returnFocus) owner.focus();
    }
    owner = null;
  }

  function open(button, fromKeyboard) {
    close(false);
    owner = button;
    var current = currentRating(button);

    list = document.createElement('div');
    list.className = 'custom-options custom-options--portal fj-filter-list';
    list.setAttribute('role', 'menu');
    list.setAttribute('aria-label', 'Filter reviews by rating');
    list.appendChild(row('All ratings', '', !current));
    RATINGS.forEach(function (v) {
      list.appendChild(row(v, v, current === v));
    });

    list.addEventListener('click', function (event) {
      var picked = event.target.closest('.custom-option');
      if (!picked) return;
      var target = owner;
      close(true);
      choose(target, picked.dataset.value);
    });

    list.addEventListener('keydown', function (event) {
      var rows = [].slice.call(list.querySelectorAll('.custom-option'));
      var i = rows.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        rows[Math.min(rows.length - 1, i + 1)].focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        rows[Math.max(0, i - 1)].focus();
      } else if (event.key === 'Escape') {
        close(true);
      }
    });

    document.body.appendChild(list);
    place(list, button);
    list.classList.add('is-open');
    button.setAttribute('aria-expanded', 'true');
    document.dispatchEvent(new CustomEvent('fj:dropdown-open', { detail: list }));
    if (fromKeyboard) (list.querySelector('.selection') || list.firstChild).focus();
  }

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest && event.target.closest(BUTTON);

      if (button && !passThrough) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (list && owner === button) close(false);
        /* detail is 0 for a click made by Enter or Space. */
        else open(button, event.detail === 0);
        return;
      }

      if (list && !list.contains(event.target)) close(false);
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && list) close(true);
  });

  /* Only one dropdown open on the whole page (Jenny 2026-09-29). Every
     dropdown (this one, the reviews filter, the reviews sort, the theme's
     custom selects) announces 'fj:dropdown-open' when it opens and closes
     itself when it hears another one's. A shared signal, because each
     script's own close-on-outside-click can be skipped: the filter button
     swallows its click before the page sees it, which left the sort list
     open underneath the filter list. */
  document.addEventListener('fj:dropdown-open', function (event) {
    if (list && event.detail !== list) close(false);
  });

  /* The list is fixed to the window, so on scroll it follows its button
     (once per frame) and only closes once the button has left the screen.
     Closing on ANY scroll shut it the instant it opened whenever the page
     was still settling from a smooth scroll or a flick. */
  var following = false;
  window.addEventListener(
    'scroll',
    function () {
      if (!list || following) return;
      following = true;
      requestAnimationFrame(function () {
        following = false;
        if (!list || !owner) return;
        var r = owner.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) close(false);
        else place(list, owner);
      });
    },
    { passive: true }
  );
  window.addEventListener('resize', function () { close(false); });
})();
